import {
	Button,
	ColumnHeader,
	IPageRange,
	ISelectOption,
	Loading,
	PageTurnOptions,
	PagingHelper,
	PagingOptions,
	Table,
} from '@aon/aon-tws-common-ui';
import * as React from 'react';
import styled from 'styled-components';
import ContentConstants from '../../contentConstants';
import { IAddedQuestion, IColumn, ICustomQuestion, IExternalAttributeVm, QuestionType } from '../../models';
import { ColumnHelper, MappedAttributeHelper, SortHelper } from '../../utilities';
import { bodyCopyLarge, Col3Col6Col3d, headlineSmall, LoadingWrapper } from '../common';
import { CustomQuestionRow } from './custom-questions';

export const Instructions = styled.p`
	${bodyCopyLarge}
	margin-top: 30px;
`;

export const CreateQuestionButton = styled(Button)``;

export const HeadlineSmall = styled.p`
	${headlineSmall}
`;
HeadlineSmall.displayName = 'HeadlineSmall';

export const NoQuestionsMessageWrapper = styled.div`
	margin-top: 140px;
	margin-bottom: 60px;
	text-align: center;
`;

export const TableContainer = styled.div`
	margin: 40px 0;
`;
export const PreContainer = styled.div`
	margin-top: 30px;
	text-align: right;
`;
const TopPagingContainer = styled.div`
	border-bottom: 1px rgb(153, 153, 153) dashed;
	margin-top: 40px;
	padding-bottom: 16px;
	width: 100%;
`;
const BottomPagingContainer = styled.div`
	float: left;
`;
export const ErrorBoxContainer = styled.div`
	margin-bottom: 40px;
	text-align: left;
`;

export interface ICustomQuestionListProps {
	addedQuestions: IAddedQuestion[];
	availableCultures: ISelectOption[];
	columns: IColumn[];
	defaultCultureCode: string;
	isDemographic: boolean;
	isFollowUp: boolean;
	loading: boolean;
	parentQuestionId?: number;
	participantAttributes: IExternalAttributeVm[];
	questions: ICustomQuestion[];
	selectedAttributes: string[];
	selectedCustomQuestionIds: number[];
	surveyId: number;
	surveySupportedCultures: string[];
	totalSelectedQuestions: number;
	onAddUpdateQuestionClick: (questionId?: number) => void;
	GetCulturesIfNeeded: () => void;
	GetCustomQuestions: (surveyId: number, defaultCultureCode: string, isDemographic: boolean) => void;
	SelectQuestion: (question: ICustomQuestion, isSelected: boolean, parentQuestionId?: number) => void;
}

export interface ICustomQuestionListState {
	pageRange: IPageRange;
}

export class CustomQuestionList extends React.PureComponent<ICustomQuestionListProps, ICustomQuestionListState> {
	constructor(props: ICustomQuestionListProps) {
		super(props);

		this.state = {
			pageRange: {
				offset: 0,
				limit: 25,
			},
		};

		this.filterQuestions = this.filterQuestions.bind(this);
		this.sortQuestions = this.sortQuestions.bind(this);
		this.updatePaging = this.updatePaging.bind(this);
	}

	public UNSAFE_componentWillMount() {
		this.props.GetCustomQuestions(this.props.surveyId, this.props.defaultCultureCode, this.props.isDemographic);
		this.props.GetCulturesIfNeeded();
	}

	public UNSAFE_componentWillReceiveProps(props: ICustomQuestionListProps) {
		if (props.questions !== this.props.questions) {
			this.setState({
				pageRange: {
					offset: 0,
					limit: 25,
					total: props.questions.length,
				},
			});
		}
	}

	public render() {
		const {
			addedQuestions,
			availableCultures,
			columns,
			isFollowUp,
			loading,
			parentQuestionId,
			participantAttributes,
			questions,
			selectedAttributes,
			selectedCustomQuestionIds,
			surveySupportedCultures,
		} = this.props;
		const { pageRange } = this.state;
		const filteredQuestions = this.filterQuestions(questions);
		const sortedQuestions = this.sortQuestions(filteredQuestions);
		const pagedQuestions = new PagingHelper<ICustomQuestion>().page(sortedQuestions, pageRange);
		const isDemographicQuestionList = this.props.isDemographic;
		const addedFollowUpQuestions = addedQuestions.filter((aq) => aq.parentQuestionId == parentQuestionId);

		return (
			<React.Fragment>
				{loading && (
					<LoadingWrapper>
						<Loading height={'70px'} width={'70px'} />
					</LoadingWrapper>
				)}
				{!loading && questions.length === 0 && (
					<NoQuestionsMessageWrapper>
						<Col3Col6Col3d>
							<HeadlineSmall>
								{isDemographicQuestionList
									? ContentConstants.SECTION_DESCRIPTION_NO_CUSTOM_DEMOGRAPHIC_QUESTIONS
									: ContentConstants.SECTION_DESCRIPTION_NO_CUSTOM_SURVEY_QUESTIONS}
							</HeadlineSmall>
							<Instructions>
								{isDemographicQuestionList
									? ContentConstants.INSTRUCTIONALTEXT_CUSTOMQUESTION_CREATEDEMOGRAPHICQUESTION
									: ContentConstants.INSTRUCTIONALTEXT_CUSTOMQUESTION_CREATESURVEYQUESTION}
							</Instructions>
							<CreateQuestionButton onClick={() => this.props.onAddUpdateQuestionClick()} size='lg' color='secondary'>
								{isDemographicQuestionList
									? ContentConstants.BUTTONS_CREATE_DEMOGRAPHIC_QUESTION
									: ContentConstants.BUTTONS_CREATE_QUESTION}
							</CreateQuestionButton>
						</Col3Col6Col3d>
					</NoQuestionsMessageWrapper>
				)}
				{!loading && questions.length > 0 && (
					<div>
						<PreContainer>
							<CreateQuestionButton
								onClick={() => this.props.onAddUpdateQuestionClick()}
								size='md'
								color='secondary'
								disabled={isFollowUp && addedFollowUpQuestions.length > 0}
							>
								{isDemographicQuestionList
									? ContentConstants.BUTTONS_CREATE_DEMOGRAPHIC_QUESTION
									: ContentConstants.BUTTONS_CREATE_QUESTION}
							</CreateQuestionButton>
						</PreContainer>
						<TopPagingContainer>
							<PagingOptions pageRange={pageRange} updatePageRange={this.updatePaging} />
						</TopPagingContainer>
						<Table responsive={true} selectable={'true'} lastalign={'right'}>
							<ColumnHeader sortedBy='questionId' sortAsc={true} columns={ColumnHelper.GetColumnHeaders(columns)} selectable={false} />
							<tbody>
								{pagedQuestions.map((question: ICustomQuestion, index: number) => {
									const mappedAttribute = MappedAttributeHelper.getMappedAttributeForCustomQuestion(
										question,
										selectedAttributes,
										selectedCustomQuestionIds,
										participantAttributes,
									);
									const isAvailable = MappedAttributeHelper.customQuestionIsAvailableForSelection(
										question,
										selectedAttributes,
										selectedCustomQuestionIds,
									);

									let disableForFollowUp = false;
									// Disable rows of selected custom questions before making a selection.
									if (addedFollowUpQuestions.length <= 0 && selectedCustomQuestionIds.indexOf(question.questionId) > -1) {
										disableForFollowUp = true;
									}
									// Disable rows non-selected rows after making a selection.
									if (
										addedFollowUpQuestions.length > 0 &&
										addedFollowUpQuestions.filter((q) => q.questionId === question.questionId).length < 1
									) {
										disableForFollowUp = true;
									}
									return (
										<CustomQuestionRow
											availableCultures={availableCultures}
											columns={columns}
											disabled={!isAvailable || disableForFollowUp}
											key={index}
											mappedAttribute={mappedAttribute}
											parentQuestionId={parentQuestionId}
											question={question}
											selectedCustomQuestionIds={selectedCustomQuestionIds}
											surveySupportedCultures={surveySupportedCultures}
											editQuestion={(questionId: number) => this.props.onAddUpdateQuestionClick(questionId)}
											SelectQuestion={this.props.SelectQuestion}
										/>
									);
								})}
							</tbody>
						</Table>
						<BottomPagingContainer>
							<PageTurnOptions pageRange={pageRange} updatePageRange={this.updatePaging} />
						</BottomPagingContainer>
					</div>
				)}
			</React.Fragment>
		);
	}

	public updatePaging(pageRange: IPageRange) {
		this.setState({ pageRange });
	}

	public sortQuestions(questions: ICustomQuestion[]) {
		const sortHelper = new SortHelper<ICustomQuestion>();
		sortHelper.AddSortProperty(
			(q: ICustomQuestion) => !(q.questionId && this.props.selectedCustomQuestionIds.indexOf(q.questionId) > -1) + '',
			'asc',
		);
		sortHelper.AddSortProperty((q: ICustomQuestion) => q.questionId + '', 'desc');
		return sortHelper.Sort(questions);
	}

	private filterQuestions(questions: ICustomQuestion[]) {
		const { isFollowUp } = this.props;

		// Unlike library questions, custom questions don't have tags to use as a filter. Response option & question type analysis will have to do for now.
		if (isFollowUp) {
			return questions.filter(
				// Custom follow up questions are only either checkbox or scale. They will also not have response options configured.
				(q) =>
					(q.questionType === QuestionType.Checkbox || q.questionType === QuestionType.Scale) &&
					(q.responseOptions === null || q.responseOptions === undefined),
			);
		} else {
			return questions.filter(
				// Custom questions that are not follow-up questions will not meet this criteria.
				(q) =>
					!(
						(q.questionType === QuestionType.Checkbox || q.questionType === QuestionType.Scale) &&
						(q.responseOptions === null || q.responseOptions === undefined)
					),
			);
		}
	}
}
